import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add,
  Delete,
  Edit,
  ExpandMore,
  KeyboardArrowRight,
  Warning,
  AcUnit,
} from "@mui/icons-material";
import { TherapyType } from "../../../api-client";
import { useCatalogMethods } from "../../../stores/MasterTables/therapies.queries";
import { useDeleteCatalogMethod } from "../../../stores/MasterTables/therapies.mutations";
import ModalMethod from "./ModalMethod";
import { ColorSwatch } from "./ColorField";

/**
 * "Methoden" — the tarif-agnostic middle level.
 *
 * A method is the therapy itself, independent of who pays for it. Each method
 * has one billable row per tarif group and discipline, which is why the same
 * method can appear as GKV_HB and GKV_ergo_HB. Showing those rows grouped under
 * their method is the only place that relationship is visible.
 */

interface Props {
  therapyType: TherapyType;
}

const MethodsTab: React.FC<Props> = ({ therapyType }) => {
  const { data: methods, isLoading } = useCatalogMethods(therapyType);
  const deleteMethod = useDeleteCatalogMethod();

  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<string | null>(null);
  const [editing, setEditing] = useState<any | null>(null);
  const [creating, setCreating] = useState(false);

  const filtered = useMemo(() => {
    const list: any[] = (methods as any[]) ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (m) =>
        m.id.toLowerCase().includes(q) ||
        m.label.toLowerCase().includes(q) ||
        (m.lineItems ?? []).some((i: any) => i.id.toLowerCase().includes(q))
    );
  }, [methods, search]);

  const handleDelete = (method: any) => {
    const blockers: string[] = [];
    if ((method.lineItems ?? []).length) blockers.push(`${method.lineItems.length} Abrechnungszeile(n)`);
    if ((method.terms ?? []).length) blockers.push(`${method.terms.length} Begriff(e)`);
    const warning = blockers.length
      ? `\n\nAchtung: verwendet von ${blockers.join(" und ")} — der Server wird das Löschen ablehnen.`
      : "";
    if (!window.confirm(`Methode "${method.id}" löschen?${warning}`)) return;
    deleteMethod.mutate(method.id);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
        <TextField
          size="small"
          label="Suchen (Methode, Abrechnungszeile)"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 340 }}
        />
        <Box sx={{ flex: 1 }} />
        <Typography variant="body2" color="text.secondary">
          {filtered.length} von {(methods as any[])?.length ?? 0}
        </Typography>
        <Button variant="contained" size="small" startIcon={<Add />} onClick={() => setCreating(true)}>
          Methode anlegen
        </Button>
      </Stack>

      <Alert severity="info" sx={{ mb: 2 }}>
        Die Methode ist das Heilmittel <strong>ohne</strong> Kostenträger. Je Tarifgruppe und
        Disziplin gibt es dazu eine Abrechnungszeile — deshalb existiert dieselbe Methode z. B.
        als GKV_HB und GKV_ergo_HB.
      </Alert>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={40} />
              <TableCell>Methode</TableCell>
              <TableCell>Bezeichnung</TableCell>
              <TableCell align="center" width={70}>
                Farbe
              </TableCell>
              <TableCell align="center">Abrechnungszeilen</TableCell>
              <TableCell align="center">Begriffe</TableCell>
              <TableCell align="center">Passiv</TableCell>
              <TableCell align="right" width={110}>
                Aktionen
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((method) => {
              const noItems = (method.lineItems ?? []).length === 0;
              const noTerms = (method.terms ?? []).length === 0;
              const isOpen = expanded === method.id;
              return (
                <React.Fragment key={method.id}>
                  <TableRow
                    hover
                    sx={{
                      "& > *": { borderBottom: isOpen ? "unset" : undefined },
                      bgcolor: noItems || noTerms ? "warning.50" : undefined,
                    }}
                  >
                    <TableCell>
                      <IconButton size="small" onClick={() => setExpanded(isOpen ? null : method.id)}>
                        {isOpen ? <ExpandMore /> : <KeyboardArrowRight />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: "monospace", fontWeight: 600 }}>
                        {method.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{method.label}</Typography>
                      {method.description && (
                        <Typography variant="caption" color="text.secondary">
                          {method.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <ColorSwatch color={method.color} />
                    </TableCell>
                    <TableCell align="center">
                      {noItems ? (
                        <Tooltip title="Keine abrechenbare Zeile — Auflösungen auf diese Methode laufen ins Leere">
                          <Chip icon={<Warning />} label="keine" size="small" color="warning" />
                        </Tooltip>
                      ) : (
                        <Chip label={method.lineItems.length} size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {noTerms ? (
                        <Tooltip title="Kein Begriff löst auf diese Methode auf — sie ist von einer Verordnung aus nicht erreichbar">
                          <Chip icon={<Warning />} label="keiner" size="small" color="warning" />
                        </Tooltip>
                      ) : (
                        <Chip label={method.terms.length} size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {method.isPassive && (
                        <Tooltip title="Passive Maßnahme (z. B. Wärme) — zählt in Regeln anders als aktive Therapie">
                          <AcUnit fontSize="small" color="info" />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => setEditing(method)}>
                        <Edit fontSize="small" />
                      </IconButton>
                      <Tooltip
                        title={
                          !noItems || !noTerms ? "Wird verwendet — Löschen wird abgelehnt" : "Löschen"
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(method)}
                            disabled={deleteMethod.isPending}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell sx={{ py: 0 }} colSpan={8}>
                      <Collapse in={isOpen} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 2, pl: 4 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Abrechnungszeilen
                          </Typography>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                            {noItems && (
                              <Typography variant="body2" color="text.secondary">
                                keine
                              </Typography>
                            )}
                            {(method.lineItems ?? []).map((i: any) => (
                              <Tooltip
                                key={i.id}
                                title={`Tarifgruppe ${i.tarifGroupId ?? "?"} · ${
                                  i.therapyType ?? "alle Disziplinen"
                                } · Positionsnummer ${i.lineItem ?? "—"}`}
                              >
                                <Chip
                                  label={i.id}
                                  size="small"
                                  variant={i.inUse ? "filled" : "outlined"}
                                  color={i.inUse ? "success" : "default"}
                                />
                              </Tooltip>
                            ))}
                          </Stack>

                          <Typography variant="subtitle2" gutterBottom>
                            Begriffe, die hierauf auflösen
                          </Typography>
                          {noTerms && (
                            <Typography variant="body2" color="text.secondary">
                              keiner
                            </Typography>
                          )}
                          <Stack spacing={0.75}>
                            {(method.terms ?? []).map((t: any) => (
                              <Stack
                                key={t.prescribedTherapyId}
                                direction="row"
                                spacing={0.5}
                                alignItems="center"
                                flexWrap="wrap"
                                useFlexGap
                              >
                                <Chip
                                  label={`${t.prescribedTherapyId} (Prio ${t.priority})`}
                                  size="small"
                                  variant="outlined"
                                  color={t.priority >= 100 ? "primary" : "default"}
                                />
                                <Typography variant="body2" color="text.secondary">
                                  {t.label}
                                </Typography>
                                {/* The spellings that actually reach this method — the
                                    question when checking why a prescription resolved
                                    the way it did. */}
                                {(t.aliases ?? []).length === 0 ? (
                                  <Tooltip title="Ohne Schreibweise wird dieser Begriff nie erkannt">
                                    <Chip
                                      icon={<Warning />}
                                      label="keine Schreibweise"
                                      size="small"
                                      color="warning"
                                    />
                                  </Tooltip>
                                ) : (
                                  (t.aliases ?? []).map((a: string) => (
                                    <Chip
                                      key={a}
                                      label={a}
                                      size="small"
                                      sx={{ fontFamily: "monospace" }}
                                    />
                                  ))
                                )}
                              </Stack>
                            ))}
                          </Stack>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {(creating || editing) && (
        <ModalMethod
          open
          method={editing}
          methods={(methods as any[]) ?? []}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
    </Box>
  );
};

export default MethodsTab;
